import { FormikProvider, useFormik } from 'formik'
import { dollarsToCents } from '@/utils/currencyHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { getFetchError } from '@/reducers/fetch'
import { useAnalyticsTrack } from '@/features/amplitude'
import { getFinancialProfile } from '@/selectors/user.selectors'

import { Alert, Text } from '@/ui'
import { TypographyStyles } from '@/styles'

import {
  UPDATE_FINANCIAL_PROFILE_KEY,
  updateFinancialProfile,
} from '@/actions/financialProfileActions'
import {
  TAX_PROFILE_STEP,
  TaxesProfileFlowAnswerProps,
  useTaxProfileSteps,
} from './helpers'
import { TaxProfileFormFooter } from './TaxProfileFormFooter'
import { W2Job } from './W2Job'
import {
  TaxProfileAccordions,
  useTaxProfileAccordionCopy,
} from './TaxProfileAccordions'

export const ScorpAdditionalW2Income = ({
  formFlowAnswers,
  setFormFlowAnswers,
}: TaxesProfileFlowAnswerProps) => {
  const dispatch = useAppDispatch()
  const track = useAnalyticsTrack()
  const fp = useReselector(getFinancialProfile)
  const error = useReselector(
    getFetchError,
    UPDATE_FINANCIAL_PROFILE_KEY(fp?.id)
  )
  const { goToPreviousStep, goToNextStep } = useTaxProfileSteps(
    TAX_PROFILE_STEP.scorpAdditionalW2Income
  )

  const someIncomeEntered =
    formFlowAnswers.wIncome !== null ||
    formFlowAnswers.estimatedAnnualW2Income !== null

  const formik = useFormik({
    enableReinitialize: true,
    validateOnMount: true,
    initialValues: {
      moneyOutsideOfPractice: someIncomeEntered ? true : undefined,
      enterPaystubW2: someIncomeEntered
        ? formFlowAnswers.wIncome !== null
        : undefined,
      wIncome: formFlowAnswers.wIncome,
      estimatedAnnualW2Income: formFlowAnswers.estimatedAnnualW2Income,
    },
    onSubmit: async ({
      moneyOutsideOfPractice,
      wIncome,
      enterPaystubW2,
      estimatedAnnualW2Income,
    }) => {
      const anyWIncome =
        formFlowAnswers.scorpPracticeWIncome ||
        wIncome ||
        estimatedAnnualW2Income
      const res = await dispatch(
        updateFinancialProfile(fp?.id, {
          wIncomeInCents:
            moneyOutsideOfPractice && enterPaystubW2
              ? dollarsToCents(wIncome)
              : null,
          estimatedAnnualW2IncomeInCents:
            moneyOutsideOfPractice && !enterPaystubW2
              ? dollarsToCents(estimatedAnnualW2Income)
              : null,
          //Withholding page is skipped if they have no W2 income
          ...(!anyWIncome && {
            federalWithholdingInCents: null,
            stateWithholdingInCents: null,
          }),
        })
      )

      if (res) {
        const newAnswers = {
          ...formFlowAnswers,
          wIncome,
          estimatedAnnualW2Income,
        }

        if (moneyOutsideOfPractice) {
          if (enterPaystubW2) {
            newAnswers.estimatedAnnualW2Income = null
          } else {
            newAnswers.wIncome = null
          }
        } else {
          newAnswers.wIncome = null
          newAnswers.estimatedAnnualW2Income = null
        }

        if (!anyWIncome) {
          newAnswers.federalWithholding = null
          newAnswers.stateWithholding = null
        }

        setFormFlowAnswers(newAnswers)
        track('scorp additional w2 income submitted', {
          has_w2_income_outside_practice: moneyOutsideOfPractice ?? false,
          enter_w2_paystub: Boolean(enterPaystubW2),
          w2_income_in_cents:
            moneyOutsideOfPractice && enterPaystubW2
              ? dollarsToCents(wIncome)
              : null,
          estimated_annual_w2_income_in_cents:
            moneyOutsideOfPractice && !enterPaystubW2
              ? dollarsToCents(estimatedAnnualW2Income)
              : null,
          user_id: fp?.userId ?? null,
        })
        goToNextStep(res)
      }
    },
  })

  const { isSubmitting, isValid, submitForm } = formik

  const faqCopy = useTaxProfileAccordionCopy()

  return (
    <FormikProvider value={formik}>
      <div className="w-full space-y-6">
        <div className="max-w-3xl mx-auto text-center">
          <Text variant={TypographyStyles.DISPLAY_SM}>
            Do you have additional W-2 jobs?
          </Text>
        </div>

        <div className="max-w-3xl mx-auto">
          <Text variant={TypographyStyles.BODY_LG}>
            These are W-2 jobs outside of your private practice, e.g. working
            for a group practice, hospital, government agency, etc.
          </Text>
        </div>

        {error && (
          <div className="max-w-3xl mx-auto">
            <Alert variant="error" description={error.message} />
          </div>
        )}

        <W2Job isScorp />
        <TaxProfileAccordions
          faqs={[
            faqCopy.whyNeedToIncludeIncomeOtherJobs,
            faqCopy.whatIsW2Job,
            faqCopy.howDoIEstimate,
            faqCopy.whatIsYearToDate,
            faqCopy.whatIsGrossIncome,
          ]}
        />
        <TaxProfileFormFooter
          submitOrContinue={submitForm}
          goToPreviousStep={goToPreviousStep}
          continueDisabled={!isValid || isSubmitting}
          loading={isSubmitting}
        />
      </div>
    </FormikProvider>
  )
}
